import { useLingui } from '@lingui/react/macro';
import { TimesheetWithEntries } from '@sit/client-shared';
import {
  SummaryDateItem,
  SummaryHoursItem,
  SummaryItem,
  SummaryPillStatusItem,
  SummaryPreviewItem,
} from '@web/components/Timesheet/SummaryCards';
import { useTimesheetSummary } from '@web/containers/Timesheet/hooks/useTimesheetSummary';
import { parse } from 'date-fns';

type SummaryItemWithValue = Extract<SummaryItem, { value: unknown }>;

const makeToSummaryItem =
  <T extends SummaryItemWithValue>(typename: T['__typename']) =>
  (label: T['label'], value: T['value'], stick = false, testId?: string): T =>
    ({
      __typename: typename,
      label,
      value,
      stick,
      testId,
    }) as T;

const toDateSummaryItem = makeToSummaryItem<SummaryDateItem>('date');
const toHoursSummaryItem = makeToSummaryItem<SummaryHoursItem>('hours');
const toPillStatusSummaryItem = makeToSummaryItem<SummaryPillStatusItem>('pill-status');
const toPreviewItem = makeToSummaryItem<SummaryPreviewItem>('preview');

export const useSummaryData = (
  timesheet: Pick<TimesheetWithEntries, 'id' | 'startDate' | 'endDate' | 'status' | 'glPostDate' | 'showBillable'> | undefined,
  overrideTotals: {
    totalHours?: number;
    billableHours?: number;
    nonBillableHours?: number;
  } = {},
): SummaryItem[] => {
  const { t } = useLingui();

  const { startDate, endDate, glPostDate, status = 'NO_STATUS', showBillable, id } = timesheet || {};
  const totalsSummary = useTimesheetSummary(id);

  const { totalHours, billableHours, nonBillableHours } = {
    ...totalsSummary,
    ...overrideTotals,
  };

  return [
    ...(startDate ? [toDateSummaryItem(t`Begin date`, parse(startDate, 'yyyy-MM-dd', new Date()))] : [toPreviewItem(t`Begin date`)]),
    ...(endDate ? [toDateSummaryItem(t`End date`, parse(endDate, 'yyyy-MM-dd', new Date()))] : [toPreviewItem(t`End date`)]),
    toHoursSummaryItem(t`Total hours`, totalHours, undefined, 'total-hours'),
    ...(showBillable
      ? [
          toHoursSummaryItem(t`Billable hours`, billableHours, undefined, 'total-billable'),
          toHoursSummaryItem(t`Non-billable hours`, nonBillableHours, undefined, 'total-non-billable'),
        ]
      : []),
    ...(glPostDate ? [toDateSummaryItem(t`GL post date`, parse(glPostDate, 'yyyy-MM-dd', new Date()))] : [toPreviewItem(t`GL post date`)]),
    toPillStatusSummaryItem(t`Timesheet status`, status),
  ] as const;
};
